/**
 * Adjust Panel Component (canvas-v2)
 *
 * Provides speed and volume controls for the selected clip.
 * - Speed slider: 0.1x - 4.0x (step 0.1) with preset buttons (0.5x, 1.0x, 1.5x, 2.0x)
 * - Volume slider: 0 - 1 (audio clips only)
 * - Mute toggle (audio clips only)
 * - Clip info display
 * - Empty state: "Select a clip to adjust."
 */

import { memo, useCallback } from 'react'
import type { TimelineClip, TimelineAudioClip } from './types'

interface AdjustPanelProps {
  /** Currently selected video clip (null if audio or none) */
  selectedVideoClip: TimelineClip | null

  /** Currently selected audio clip (null if video or none) */
  selectedAudioClip: TimelineAudioClip | null

  /** Type of selected clip */
  selectedClipType: 'video' | 'audio' | null

  /** Callback when clip speed changes */
  onSpeedChange: (clipId: string, speed: number) => void

  /** Callback when audio volume changes */
  onVolumeChange: (clipId: string, volume: number) => void

  /** Callback when audio mute is toggled */
  onToggleMute: (clipId: string) => void
}

const SPEED_PRESETS = [0.5, 1.0, 1.5, 2.0]

export const AdjustPanel = memo(function AdjustPanel({
  selectedVideoClip,
  selectedAudioClip,
  selectedClipType,
  onSpeedChange,
  onVolumeChange,
  onToggleMute,
}: AdjustPanelProps) {
  const clip = selectedClipType === 'video' ? selectedVideoClip : selectedAudioClip
  const audioClip = selectedClipType === 'audio' ? selectedAudioClip : null

  const handleSpeedSliderChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!clip) return
      const speed = parseFloat(e.target.value)
      onSpeedChange(clip.id, speed)
    },
    [clip, onSpeedChange]
  )

  const handleSpeedPreset = useCallback(
    (speed: number) => {
      if (!clip) return
      onSpeedChange(clip.id, speed)
    },
    [clip, onSpeedChange]
  )

  const handleVolumeChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!audioClip) return
      const volume = parseFloat(e.target.value)
      onVolumeChange(audioClip.id, volume)
    },
    [audioClip, onVolumeChange]
  )

  const handleToggleMute = useCallback(() => {
    if (!audioClip) return
    onToggleMute(audioClip.id)
  }, [audioClip, onToggleMute])

  // Empty state
  if (!clip) {
    return (
      <div className="flex items-center justify-center h-full text-text-muted text-sm p-4">
        Select a clip to adjust.
      </div>
    )
  }

  const currentSpeed = clip.speed

  return (
    <div className="flex flex-col gap-4 p-3 h-full overflow-y-auto">
      {/* Clip info */}
      <div className="border-b border-border pb-3">
        <div className="text-foreground text-sm font-medium truncate">
          {selectedClipType === 'video'
            ? (clip as TimelineClip).label
            : clip.sourcePath.split('/').pop() || 'Audio'
          }
        </div>
        <div className="text-text-muted text-xs mt-0.5">
          {selectedClipType === 'video' ? 'Video clip' : 'Audio clip'}
          {' | '}
          {formatDuration(clip.effectiveDuration)}
        </div>
      </div>

      {/* Speed control */}
      <div>
        <label className="text-text-secondary text-xs font-medium block mb-2">
          Speed
        </label>

        {/* Speed slider */}
        <div className="flex items-center gap-2">
          <input
            type="range"
            min="0.1"
            max="4.0"
            step="0.1"
            value={currentSpeed}
            onChange={handleSpeedSliderChange}
            className="flex-1"
          />
          <span className="text-foreground text-sm font-mono min-w-[4ch] text-right">
            {currentSpeed.toFixed(1)}x
          </span>
        </div>

        {/* Speed presets */}
        <div className="flex gap-1.5 mt-2">
          {SPEED_PRESETS.map((preset) => (
            <button
              key={preset}
              onClick={() => handleSpeedPreset(preset)}
              className={`
                flex-1 px-2 py-1 text-xs rounded border transition-colors
                ${Math.abs(currentSpeed - preset) < 0.05
                  ? 'bg-blue-500/20 border-blue-400 text-blue-300'
                  : 'bg-card border-border text-text-secondary hover:bg-card-hover'
                }
              `}
            >
              {preset}x
            </button>
          ))}
        </div>
      </div>

      {/* Volume control (audio only) */}
      {audioClip && (
        <div>
          <label className="text-text-secondary text-xs font-medium block mb-2">
            Volume
          </label>

          <div className="flex items-center gap-2">
            {/* Mute toggle */}
            <button
              onClick={handleToggleMute}
              className={`
                p-1.5 rounded border transition-colors text-sm
                ${audioClip.muted
                  ? 'bg-red-500/20 border-red-400 text-red-300'
                  : 'bg-card border-border text-text-secondary hover:bg-card-hover'
                }
              `}
              title={audioClip.muted ? 'Unmute' : 'Mute'}
            >
              {audioClip.muted ? (
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2" />
                </svg>
              ) : (
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
                </svg>
              )}
            </button>

            {/* Volume slider */}
            <input
              type="range"
              min="0"
              max="1"
              step="0.01"
              value={audioClip.volume}
              onChange={handleVolumeChange}
              className="flex-1"
              disabled={audioClip.muted}
            />
            <span className="text-foreground text-sm font-mono min-w-[4ch] text-right">
              {Math.round(audioClip.volume * 100)}%
            </span>
          </div>
        </div>
      )}

      {/* Additional clip details */}
      <div className="mt-auto border-t border-border pt-3">
        <div className="text-text-muted text-xxs space-y-1">
          <div className="flex justify-between">
            <span>Original duration</span>
            <span>{formatDuration(clip.originalDuration)}</span>
          </div>
          <div className="flex justify-between">
            <span>Trim start</span>
            <span>{clip.trimStart.toFixed(2)}s</span>
          </div>
          <div className="flex justify-between">
            <span>Trim end</span>
            <span>{clip.trimEnd.toFixed(2)}s</span>
          </div>
          {audioClip && (
            <div className="flex justify-between">
              <span>Start offset</span>
              <span>{audioClip.startOffset.toFixed(2)}s</span>
            </div>
          )}
        </div>
      </div>
    </div>
  )
})

function formatDuration(seconds: number): string {
  if (seconds < 60) {
    return `${seconds.toFixed(1)}s`
  }
  const mins = Math.floor(seconds / 60)
  const secs = Math.floor(seconds % 60)
  return `${mins}:${secs.toString().padStart(2, '0')}`
}
